import React from "react";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";

const AnalyticsSection: React.FC = () => {
  const { ref, inView } = useInView({
    threshold: 0.1,
    triggerOnce: true,
  });

  const analyticsFeatures = [
    {
      title: "Real-time Dashboard",
      description:
        "Live monitoring of employee activities, productivity metrics, and performance indicators.",
      metrics: [
        "Live Activity",
        "Productivity Score",
        "Focus Time",
        "Break Analysis",
      ],
    },
    {
      title: "Advanced Reporting",
      description:
        "Comprehensive reports with insights, trends, and recommendations for optimization.",
      metrics: [
        "Weekly Reports",
        "Monthly Analytics",
        "Custom Reports",
        "Export Options",
      ],
    },
    {
      title: "Predictive Analytics",
      description:
        "AI-powered insights to predict trends and suggest improvements for better outcomes.",
      metrics: [
        "Trend Analysis",
        "Performance Prediction",
        "Risk Assessment",
        "Optimization Tips",
      ],
    },
  ];

  return (
    <section
      ref={ref}
      className="section-padding bg-gradient-to-br from-dark-950 via-dark-900 to-dark-800 text-white"
    >
      <div className="container-max">
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
        >
          <h2 className="text-4xl sm:text-5xl font-bold mb-6">
            High-End Analytics for
            <span className="block text-transparent bg-clip-text bg-gradient-to-r from-primary-400 to-secondary-400">
              Better Decisions
            </span>
          </h2>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Transform raw data into actionable insights with our advanced
            analytics platform. Make informed decisions that drive your
            organization forward.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-16">
          {analyticsFeatures.map((feature, index) => (
            <motion.div
              key={index}
              className="glass-card p-8"
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: index * 0.2 }}
            >
              <h3 className="text-2xl font-bold mb-4 text-white">
                {feature.title}
              </h3>
              <p className="text-gray-300 mb-6 leading-relaxed">
                {feature.description}
              </p>
              <div className="space-y-2">
                {feature.metrics.map((metric, metricIndex) => (
                  <div
                    key={metricIndex}
                    className="flex items-center text-gray-300"
                  >
                    <div className="w-2 h-2 bg-primary-400 rounded-full mr-3" />
                    {metric}
                  </div>
                ))}
              </div>
            </motion.div>
          ))}
        </div>

        {/* Mock Dashboard Preview */}
        <motion.div
          className="relative"
          initial={{ opacity: 0, scale: 0.95 }}
          animate={inView ? { opacity: 1, scale: 1 } : {}}
          transition={{ duration: 1, delay: 0.6 }}
        >
          <div className="glass-card p-8">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-2xl font-bold text-white">
                Analytics Dashboard
              </h3>
              <div className="flex space-x-2">
                <div className="w-3 h-3 bg-red-400 rounded-full" />
                <div className="w-3 h-3 bg-yellow-400 rounded-full" />
                <div className="w-3 h-3 bg-green-400 rounded-full" />
              </div>
            </div>

            {/* Mock Charts */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
              <div className="bg-gradient-to-br from-primary-500 to-primary-600 rounded-xl p-6 text-white">
                <div className="text-3xl font-bold mb-2">87%</div>
                <div className="text-primary-100">Productivity</div>
              </div>
              <div className="bg-gradient-to-br from-secondary-500 to-secondary-600 rounded-xl p-6 text-white">
                <div className="text-3xl font-bold mb-2">6.2h</div>
                <div className="text-secondary-100">Focus Time</div>
              </div>
              <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl p-6 text-white">
                <div className="text-3xl font-bold mb-2">94%</div>
                <div className="text-green-100">Goal Achievement</div>
              </div>
              <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl p-6 text-white">
                <div className="text-3xl font-bold mb-2">23</div>
                <div className="text-purple-100">Active Users</div>
              </div>
            </div>

            {/* Mock Chart Area */}
            <div className="bg-dark-800/50 backdrop-blur-sm rounded-xl p-6 h-64 flex items-center justify-center border border-dark-600/30">
              <div className="text-center text-gray-400">
                <div className="text-4xl mb-4">📊</div>
                <div className="text-lg font-semibold">
                  Interactive Analytics Chart
                </div>
                <div className="text-sm">Real-time data visualization</div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* CTA */}
        <motion.div
          className="text-center mt-16"
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.8 }}
        >
          <h3 className="text-3xl font-bold mb-4">
            Ready to Unlock Your Data's Potential?
          </h3>
          <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
            Join the waitlist and be among the first to experience our powerful
            analytics platform.
          </p>
          <button className="btn-primary text-lg px-8 py-4">
            Join Waitlist Now
          </button>
        </motion.div>
      </div>
    </section>
  );
};

export default AnalyticsSection;
